import { jsxs, jsx } from 'react/jsx-runtime';
import { useMutationTagCreate } from '../mutations/useMutationTagCreate.js';
import { useMutationTagUpdate } from '../mutations/useMutationTagUpdate.js';
import { useState, useMemo, useCallback } from 'react';
import { styled } from 'styled-components';
import { IconEnter } from '@fabric-app/icons/old';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { Label } from '@fabric-app/ui/components/Label';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { useRefocusOnBlur } from '@fabric-app/hooks/useRefocusOnBlur';
import { TextInput } from '@fabric-app/ui/components/TextInput/TextInput';
import { Kbd } from '@fabric-app/ui/components/Kbd';

const StyledLabel = styled(Label).withConfig({
  displayName: "CreateOrRenameTagModal__StyledLabel",
  componentId: "sc-a82c3433-0"
})([
  `font-weight:bold;`
]);
const CreateOrRenameTagModalContent = ({ tag, onClose }) => {
  const tagNameInput = useInputControls(tag?.name);
  const { mutate: mutateTagCreate } = useMutationTagCreate();
  const { mutate: mutateTagUpdate } = useMutationTagUpdate();
  const { isMobileView } = useResponsive();
  const onSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (tag) {
      mutateTagUpdate({
        tagId: tag.id,
        newName: tagNameInput.value
      });
    } else {
      mutateTagCreate({
        tagName: tagNameInput.value
      });
    }
    onClose();
  }, [
    mutateTagCreate,
    mutateTagUpdate,
    tagNameInput.value,
    tag,
    onClose
  ]);
  const onBlurInput = useRefocusOnBlur(isMobileView);
  return /* @__PURE__ */ jsxs("form", {
    onSubmit,
    children: [
      /* @__PURE__ */ jsx(Modal.Header, {
        children: tag ? "Rename tag" : "Create new tag"
      }),
      /* @__PURE__ */ jsxs(Modal.Body, {
        children: [
          /* @__PURE__ */ jsx(Modal.Description, {
            children: "Tags let you add keywords to anything in Fabric, making them easier to find and browse"
          }),
          /* @__PURE__ */ jsxs("div", {
            className: "grid gap-3",
            children: [
              /* @__PURE__ */ jsx(StyledLabel, {
                htmlFor: "tag-name",
                children: "Name"
              }),
              /* @__PURE__ */ jsx(TextInput, {
                id: "tag-name",
                name: "tag-name",
                "data-1p-ignore": true,
                autoComplete: "off",
                placeholder: "Tag name",
                value: tagNameInput.value,
                onChange: tagNameInput.onChange,
                autoFocus: true,
                onBlur: onBlurInput,
                inputHeight: "lg"
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsxs(Modal.Footer, {
        children: [
          /* @__PURE__ */ jsxs(Modal.CancelButton, {
            onClick: onClose,
            color: "transparent",
            children: [
              "Cancel ",
              /* @__PURE__ */ jsx(Kbd, {
                children: "ESC"
              })
            ]
          }),
          /* @__PURE__ */ jsxs(Modal.Button, {
            type: "submit",
            "data-testid": "modal-confirm-button",
            children: [
              "Save",
              /* @__PURE__ */ jsx(Kbd, {
                children: /* @__PURE__ */ jsx(IconEnter, {})
              })
            ]
          })
        ]
      })
    ]
  });
};
const CreateOrRenameTagModal = ({ tag, children, triggerAsChild = false, noTrigger = false, open: openProp, setOpen: setOpenProp }) => {
  const [_open, _setOpen] = useState(false);
  const open = useMemo(() => openProp ?? _open, [
    openProp,
    _open
  ]);
  const setOpen = useMemo(() => setOpenProp ?? _setOpen, [
    setOpenProp,
    _setOpen
  ]);
  return /* @__PURE__ */ jsxs(Modal.Root, {
    open,
    onOpenChange: setOpen,
    children: [
      !noTrigger && /* @__PURE__ */ jsx(Modal.Trigger, {
        asChild: triggerAsChild,
        children
      }),
      /* @__PURE__ */ jsxs(Modal.Portal, {
        container: getDefaultRadixPortalContainer(),
        children: [
          /* @__PURE__ */ jsx(Modal.Overlay, {}),
          /* @__PURE__ */ jsx(Modal.Content, {
            respectMobileKeyboard: true,
            children: /* @__PURE__ */ jsx(CreateOrRenameTagModalContent, {
              tag,
              onClose: () => setOpen(false)
            })
          })
        ]
      })
    ]
  });
};

export { CreateOrRenameTagModal as default };
//# sourceMappingURL=CreateOrRenameTagModal.js.map
